import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BOT_PROFILES } from "@/lib/botProfiles";

const getSettingCacheKey = (key: string) => `platform-setting-cache:${key}`;
const SETTINGS_QUERY_TIMEOUT_MS = 7000;

function isBotSafetyCriticalKey(key: string) {
  return key === "bots_global_enabled" || key.startsWith("bot_");
}

function isGuestPublicSettingKey(key: string) {
  return key === "guest_access_enabled" || key === "guest_chat_enabled" || key === "guest_form_visible";
}

function isExplicitlyEnabled(value: unknown) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "on", "yes"].includes(normalized)) return true;
    if (["false", "0", "off", "no"].includes(normalized)) return false;
  }
  return false;
}

function resolveBotTargetCount(overrideValue: unknown, autoMaxValue: unknown): number | null {
  if (typeof overrideValue === "number" && Number.isFinite(overrideValue)) {
    return Math.max(0, overrideValue);
  }

  const isAuto = overrideValue === "auto" || (overrideValue === null && typeof autoMaxValue === "number");

  if (isAuto && typeof autoMaxValue === "number" && Number.isFinite(autoMaxValue)) {
    return Math.max(0, autoMaxValue);
  }

  return null;
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs = SETTINGS_QUERY_TIMEOUT_MS): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      const timeout = setTimeout(() => {
        clearTimeout(timeout);
        reject(new Error("Settings request timeout"));
      }, timeoutMs);
    }),
  ]);
}

type CachedSettingSnapshot = {
  value: any;
  cachedAt: number;
};

function readCachedSettingSnapshot(key: string): CachedSettingSnapshot | undefined {
  if (typeof window === "undefined") return undefined;
  if (isBotSafetyCriticalKey(key)) return undefined;

  try {
    const raw = window.localStorage.getItem(getSettingCacheKey(key));
    if (!raw) return undefined;

    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "value" in parsed) {
      return {
        value: parsed.value,
        cachedAt: typeof parsed.cachedAt === "number" ? parsed.cachedAt : 0,
      };
    }

    return {
      value: parsed,
      cachedAt: 0,
    };
  } catch {
    return undefined;
  }
}

function readCachedSetting(key: string) {
  return readCachedSettingSnapshot(key)?.value;
}

function writeCachedSetting(key: string, value: any) {
  if (typeof window === "undefined") return;
  if (isBotSafetyCriticalKey(key)) return;

  try {
    window.localStorage.setItem(
      getSettingCacheKey(key),
      JSON.stringify({ value, cachedAt: Date.now() })
    );
  } catch {
    // ignore storage errors
  }
}

const LIVE_SYNC_SETTING_KEYS = new Set([
  "bots_global_enabled",
  "bot_presence_enabled",
  "bot_chat_enabled",
  "bot_pm_male_enabled",
  "bot_pm_female_enabled",
  "bot_auto_max_count",
  "bot_count_override",
  "bot_active_roster",
  "bot_rotation_enabled",
  "bot_rotation_interval_minutes",
  "bot_rotation_percentage",
  "guest_access_enabled",
  "guest_chat_enabled",
  "guest_form_visible",
  "default_chat_theme",
  "hide_coin_shop",
  "daily_bonus_enabled",
]);

type SettingBusListener = (payload: { key?: string; value?: unknown }) => void;

let settingBusChannel: any = null;
let settingBusListeners = new Set<SettingBusListener>();

let settingBusRefCount = 0;

function subscribeToSharedSettingBus(listener: SettingBusListener) {
  settingBusListeners.add(listener);
  settingBusRefCount += 1;

  if (!settingBusChannel) {
    settingBusChannel = supabase
      .channel("platform-settings-bus")
      .on("broadcast", { event: "setting_updated" }, (msg: any) => {
        const payload = msg?.payload ?? {};
        settingBusListeners.forEach((handler) => handler(payload));
      })
      .subscribe();
  }

  return () => {
    settingBusListeners.delete(listener);
    settingBusRefCount = Math.max(0, settingBusRefCount - 1);

    if (settingBusRefCount === 0 && settingBusChannel) {
      supabase.removeChannel(settingBusChannel);
      settingBusChannel = null;
    }
  };
}

export function usePlatformSetting(key: string) {
  const isLiveSyncKey = LIVE_SYNC_SETTING_KEYS.has(key);
  const isBotCriticalKey = isBotSafetyCriticalKey(key);
  const queryClient = useQueryClient();
  const cachedSetting = isBotCriticalKey ? undefined : readCachedSettingSnapshot(key);
  const shouldUseCachedSetting = !isGuestPublicSettingKey(key) && cachedSetting?.value !== undefined;

  // Cost-optimized: platform_settings no longer in realtime publication.
  // Live-sync keys propagate via 60s polling + broadcast bus for instant admin toggles.
  useEffect(() => {
    if (!isLiveSyncKey) return;

    return subscribeToSharedSettingBus((payload) => {
        if (payload?.key !== key) return;
        const newValue = payload.value ?? null;
        writeCachedSetting(key, newValue);
        queryClient.setQueryData(["platform-setting", key], newValue);
      });
  }, [key, isLiveSyncKey, queryClient]);

  return useQuery({
    queryKey: ["platform-setting", key],
    queryFn: async () => {
      try {
        const result = await withTimeout<any>(
          (supabase as any)
            .from("platform_settings")
            .select("value")
            .eq("key", key)
            .maybeSingle(),
        );

        const { data, error } = result ?? {};

        if (error) throw error;

        const value = (data as any)?.value ?? null;
        writeCachedSetting(key, value);
        return value;
      } catch {
        if (isBotCriticalKey) return null;
        const cached = readCachedSetting(key);
        return cached !== undefined ? cached : null;
      }
    },
    initialData: shouldUseCachedSetting ? cachedSetting?.value : undefined,
    initialDataUpdatedAt: shouldUseCachedSetting ? cachedSetting?.cachedAt : undefined,
    staleTime: isLiveSyncKey ? 30000 : 300000,
    refetchOnMount: isBotCriticalKey ? "always" : true,
    refetchOnWindowFocus: false,
    refetchInterval: isLiveSyncKey ? 60000 : false,
  });
}

export function useUpdatePlatformSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      // Guard: never send null/undefined value — DB column is NOT NULL
      if (value === null || value === undefined) {
        console.warn(`[platform_settings] Skipped upsert for "${key}" — value is null/undefined`);
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const updatedAt = new Date().toISOString();

      const { error } = await (supabase as any)
        .from("platform_settings")
        .upsert(
          {
            key,
            value,
            updated_at: updatedAt,
            updated_by: user?.id ?? null,
          },
          { onConflict: "key" },
        );

      if (error) throw error;

      const isBotKillSwitch = key === "bot_presence_enabled" || key === "bots_global_enabled";
      const isDisabledValue =
        value === false ||
        value === 0 ||
        value === "0" ||
        (typeof value === "string" && ["false", "off", "no"].includes(value.trim().toLowerCase()));

      if (isBotKillSwitch && isDisabledValue) {
        const { error: rosterError } = await (supabase as any)
          .from("platform_settings")
          .upsert(
            {
              key: "bot_active_roster",
              value: [],
              updated_at: updatedAt,
              updated_by: user?.id ?? null,
            },
            { onConflict: "key" },
          );

        if (rosterError) throw rosterError;
      }

      // When admin changes bot count, immediately build the correct roster in DB
      const isBotCountChange = key === "bot_auto_max_count" || key === "bot_count_override";
      if (isBotCountChange) {
        await rebuildBotRoster(key, value, updatedAt, user?.id ?? null);
      }

      return { key, value };
    },
    onSuccess: ({ key, value }) => {
      writeCachedSetting(key, value ?? null);
      queryClient.setQueryData(["platform-setting", key], value ?? null);
      queryClient.invalidateQueries({ queryKey: ["platform-setting", key] });

      // Broadcast on bus so other clients update instantly (replaces realtime postgres_changes)
      if (LIVE_SYNC_SETTING_KEYS.has(key)) {
        const ch = supabase.channel("platform-settings-bus");
        ch.subscribe((status) => {
          if (status === "SUBSCRIBED") {
            ch.send({
              type: "broadcast",
              event: "setting_updated",
              payload: { key, value: value ?? null },
            }).finally(() => {
              setTimeout(() => supabase.removeChannel(ch), 500);
            });
          }
        });
      }
    },
  });
}

export function useGuestAccess() {
  const { data, isLoading } = usePlatformSetting("guest_access_enabled");
  return { guestAccessEnabled: data === true || data === "true", isLoading };
}

// --- Bot roster builder (runs only when admin changes bot count) ---
async function rebuildBotRoster(changedKey: string, changedValue: any, updatedAt: string, userId: string | null) {
  try {
    // Read the OTHER setting so we know the effective target
    const otherKey = changedKey === "bot_auto_max_count" ? "bot_count_override" : "bot_auto_max_count";
    const [otherResult, rosterResult, blacklistResult, globalResult, presenceResult] = await Promise.all([
      (supabase as any)
        .from("platform_settings")
        .select("value")
        .eq("key", otherKey)
        .maybeSingle(),
      (supabase as any)
        .from("platform_settings")
        .select("value")
        .eq("key", "bot_active_roster")
        .maybeSingle(),
      (supabase as any)
        .from("platform_settings")
        .select("value")
        .eq("key", "bot_blacklist")
        .maybeSingle(),
      (supabase as any)
        .from("platform_settings")
        .select("value")
        .eq("key", "bots_global_enabled")
        .maybeSingle(),
      (supabase as any)
        .from("platform_settings")
        .select("value")
        .eq("key", "bot_presence_enabled")
        .maybeSingle(),
    ]);

    const otherValue = otherResult.data?.value ?? null;

    let overrideVal: any;
    let autoMaxVal: any;
    if (changedKey === "bot_count_override") {
      overrideVal = changedValue;
      autoMaxVal = otherValue;
    } else {
      overrideVal = otherValue;
      autoMaxVal = changedValue;
    }

    const persistRoster = async (roster: { id: string; joinedAt: number }[]) => {
      await (supabase as any).from("platform_settings").upsert(
        { key: "bot_active_roster", value: roster, updated_at: updatedAt, updated_by: userId },
        { onConflict: "key" },
      );
    };

    const botsAreExplicitlyEnabled =
      isExplicitlyEnabled(globalResult.data?.value) && isExplicitlyEnabled(presenceResult.data?.value);

    if (!botsAreExplicitlyEnabled) {
      await persistRoster([]);
      return;
    }

    const targetCount = resolveBotTargetCount(overrideVal, autoMaxVal);

    if (targetCount === null || targetCount <= 0) {
      await persistRoster([]);
      return;
    }

    const currentRoster: { id: string; joinedAt: number }[] = Array.isArray(rosterResult.data?.value)
      ? rosterResult.data.value
      : [];

    const blacklist: string[] = Array.isArray(blacklistResult.data?.value) ? blacklistResult.data.value : [];
    const blacklistSet = new Set(blacklist);

    const BOT_BY_ID = new Map(BOT_PROFILES.map(b => [b.id, b]));
    // Filter valid current roster
    const validRoster = currentRoster.filter(r => r?.id && BOT_BY_ID.has(r.id) && !blacklistSet.has(r.id));

    let newRoster: { id: string; joinedAt: number }[];
    const now = Date.now();

    if (validRoster.length === targetCount) {
      // Already correct
      return;
    } else if (validRoster.length > targetCount) {
      // Trim
      newRoster = validRoster.slice(0, targetCount);
    } else {
      // Fill
      const existingIds = new Set([...validRoster.map(r => r.id), ...blacklistSet]);
      const available = BOT_PROFILES.filter(b => !existingIds.has(b.id));
      const shuffled = [...available].sort(() => Math.random() - 0.5);
      const extras = shuffled.slice(0, targetCount - validRoster.length);
      newRoster = [
        ...validRoster,
        ...extras.map((bot, idx) => ({ id: bot.id, joinedAt: now - idx * 3 * 60_000 })),
      ];
    }

    await persistRoster(newRoster);
  } catch (err) {
    console.error("[rebuildBotRoster] Failed:", err);
  }
}
